import { CloudArrowUpIcon, ExclamationTriangleIcon, FolderOpenIcon } from '@heroicons/react/24/outline';
//@ts-ignore: Missing types in `beautiful-react-hooks`
import useDebouncedCallback from 'beautiful-react-hooks/useDebouncedCallback';
import axios from 'axios';
import { get, isEmpty } from 'radash';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { Icon } from '@/components/core/Icon';
import { IconButton } from '@/components/core/IconButton';
import { Image } from '@/components/core/Image';
import { Input } from '@/components/core/Input';
import { Loader } from '@/components/core/Loader';
import { MIME_TYPES } from '@/constants';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { showAssetFoldersModal } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { getFilenameFromUrl } from '@/services/utils';
import { LinkedAsset } from '@/types/AssetFolders';

import { useAssetFolderUploader } from '../hooks/useAssetFolderUploader';
import { useAttributeLabelTranslator } from '../hooks/useAttributeLabelTranslator';
import useFocusOnAttribute from '../hooks/useFocusOnAttribute';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../hooks/useIsHiddenAttribute';
import { usePlaceholder } from '../hooks/usePlaceholder';
import { usePreviewValidator } from '../hooks/usePreviewValidator';
import { usePublicAsset } from '../hooks/usePublicAsset';
import { useValidationsStore,Validation } from '../hooks/useValidationsStore';
import { Layout } from '../Layout/Layout';
import { EditorComponent } from '../types';
import { ImageSelectDialog } from './ImageSelectDialog';
import { NonDrawableURLPreview } from './NonDrawableURLPreview';
import { PreviewDialog } from './PreviewDialog';

const ACCEPTED_IMAGE_TYPES = {
    'image/*': [],
};

interface ImageComponentProps extends EditorComponent {
    mode?: 'Document' | 'Image';
    className?: string;
}

const ImageComponent = ({
    attribute,
    direction = 'horizontal',
    gridMode = false,
    fieldPath = attribute.name,
    hidden = false,
    readonly = false,
    hideLabel = false,
    mode = 'Image',
    beforeAttributeControl,
    className
}: ImageComponentProps) => {

    const focusRef = useFocusOnAttribute<HTMLInputElement>(attribute);

    const { translateAttributeLabel } = useAttributeLabelTranslator();
    const attributeLabel = translateAttributeLabel(attribute);
    const placeholder = usePlaceholder(attribute, readonly);

    const validationErrors = useValidationsStore(useShallow(state => state.validationErrors[fieldPath] as Validation[]));
    const validationWarnings = useValidationsStore(useShallow(state => state.validationWarnings[fieldPath] as Validation[]));
    const hasErrors = !isEmpty(validationErrors);
    const hasWarnings = !isEmpty(validationWarnings);

    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    const { t } = useTranslation();
    const { toast } = useToast();

    const { validatePreviewAsync } = usePreviewValidator();

    const { register } = useFormContext();
    const { field } = useController({
        name: fieldPath,
    });
    const currentValue = useWatch({
        name: fieldPath
    }) as string;

    const [isSelectDialogOpen, setIsSelectDialogOpen] = useState(false);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [isPreviewUrlDrawable, setIsPreviewUrlDrawable] = useState(false);
    const [mimeType, setMimeType] = useState<string | undefined>();
    const [imageLoadError, setImageLoadError] = useState(false);

    const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);

    const assetFolderUploader = useAssetFolderUploader();
    const onDropAccepted = useCallback(async (acceptedFiles: File[]) => {
        setLoadingPreview(true);

        const previewUrl = await assetFolderUploader.uploadFile({
            file: acceptedFiles[0],
        });

        field.onChange(previewUrl);
        await checkIfUrlDrawableAndPreviewAsync(previewUrl);
        setLoadingPreview(false);

        //eslint-disable-next-line
    }, []);

    const onDropRejected = useCallback(() => {
        toast.error('UPLOAD.NO_IMAGE_ERROR');
    }, [toast]);

    const {
        getRootProps: getDropZoneProps,
        getInputProps: getDropZoneInputProps,
        isDragActive,
    } = useDropzone({
        onDropAccepted,
        onDropRejected,
        maxFiles: 1,
        noClick: true,
        accept: mode === 'Image' ? ACCEPTED_IMAGE_TYPES : undefined,
    });

    const dropZoneProps = attribute.params?.disableDragAndDrop ? {} : getDropZoneProps();
    const dropZoneInputProps = attribute.params?.disableDragAndDrop ? {} : getDropZoneInputProps();

    /**
     * FIXME: Update when we have asset folders in the next-app
     *
     * Open up the asset folders modal through the AngularJS bridge and set
     * the `publicAssetUrl`/`privateAssetUrl` as the attribute value when selected.
     */
    const { convertToPublicAsset } = usePublicAsset();
    const openAssetFoldersModal = () => {

        let acceptedTypes: string;

        if (mode === 'Image') {
            acceptedTypes = [
                MIME_TYPES.IMAGE_BMP,
                MIME_TYPES.IMAGE_GIF,
                MIME_TYPES.IMAGE_JPEG,
                MIME_TYPES.IMAGE_PNG,
                MIME_TYPES.IMAGE_SVG,
                MIME_TYPES.IMAGE_WEBP,
            ].join(',');
        } else {
            // Join all mime types
            acceptedTypes = Object.keys(MIME_TYPES).reduce((result, mimeType) => result += `,${get(MIME_TYPES, mimeType)}`, '');
        }

        showAssetFoldersModal(
            async (linkedAsset: LinkedAsset, linkedAssetPath: string) => {

                let url: string;

                try {
                    setLoadingPreview(true);

                    if (attribute.params?.confidential) {
                        url = linkedAsset.privateAssetUrl;
                    } else if (linkedAsset.publicAssetUrl) {
                        url = linkedAsset.publicAssetUrl;
                    } else {
                        url = await convertToPublicAsset(linkedAssetPath);
                    }

                    field.onChange(url);
                    await checkIfUrlDrawableAndPreviewAsync(url);

                } catch(exception) {
                    logger.error('ImageComponent: Could not select an asset via asset folders', exception);
                    setImageLoadError(true);
                } finally {
                    setLoadingPreview(false);
                }
            },
            acceptedTypes
        );
    };

    const onChange = (newUrl: string) => {
        async function checkNewUrl() {
            if (isEmpty(newUrl)) {
                setImageLoadError(false);
                setLoadingPreview(false);
            } else {
                setImageLoadError(false);
                setLoadingPreview(true);
                const completed = await checkIfUrlDrawableAndPreviewAsync(newUrl);
                if (completed) {
                    setLoadingPreview(false);
                }
            }
        }

        checkNewUrl();
    };
    const debouncedOnChange = useDebouncedCallback(onChange, [currentValue]) as (newUrl: string) => void;

    const onViewModelChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        field.onChange(value);
        debouncedOnChange(value);
    };

    const onLoadingImageError = () => {
        setImageLoadError(true);
    };

    const checkIfUrlDrawableAndPreviewAsync = async (url: string): Promise<boolean> => {
        try {
            const { mimeType, drawable } = await validatePreviewAsync(url);
            setMimeType(mimeType);
            setIsPreviewUrlDrawable(drawable);
            return true;
        } catch (error) {
            if (axios.isCancel(error)) {
                return false;
            }
            setMimeType(undefined);
            setIsPreviewUrlDrawable(false);
            setImageLoadError(true);
            return true;
        }
    };

    const showFullUrl = () => {
        // FIXME: need to add this feature
    };

    useEffect(() => {
        async function checkInitialInput() {
            if (currentValue) {
                setLoadingPreview(true);
                setImageLoadError(false);
                const completed = await checkIfUrlDrawableAndPreviewAsync(currentValue);
                if (completed) {
                    setLoadingPreview(false);
                }
            }
        }

        checkInitialInput();
        //eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (currentValue) {
            debouncedOnChange(currentValue);
        }
    }, [currentValue, debouncedOnChange]);

    if (hidden) {
        return null;
    }

    if (gridMode) {

        return (
            <div className="tw-flex tw-justify-center">

                {
                    loadingPreview &&
                    <Loader />
                }

                {
                    isPreviewUrlDrawable ?
                        <Image
                            className="tw-w-[20px] tw-h-[20px] tw-object-contain tw-cursor-pointer"
                            alt={fieldPath}
                            src={currentValue}
                            onError={onLoadingImageError}
                            onClick={() => setIsSelectDialogOpen(true)}
                        /> :
                        mimeType && <span className="tw-cursor-pointer" onClick={() => setIsSelectDialogOpen(true)}>
                            <NonDrawableURLPreview mimeType={mimeType} className='tw-scale-[2]'/>
                        </span>
                }

                {
                    imageLoadError &&
                    <>
                        <Icon
                            icon={ExclamationTriangleIcon}
                            size="lg"
                            variant="destructive"
                            colorScheme="dark"
                        />
                        <span className="tw-text-error-dark tw-text-xs">
                            {t('FILE_NOT_FOUND')}
                        </span>
                    </>
                }

                {
                    !currentValue &&
                    <IconButton
                        icon={CloudArrowUpIcon}
                        size="sm"
                        type="button"
                        className={className}
                        onClick={() => setIsSelectDialogOpen(true)}
                    />
                }

                {
                    isSelectDialogOpen &&
                    <ImageSelectDialog
                        attribute={attribute}
                        initialLink={currentValue}
                        title={attributeLabel}
                        onApply={(url) => { field.onChange(url); setIsSelectDialogOpen(false); }}
                        onClose={() => setIsSelectDialogOpen(false)}
                        mode={mode}
                        readonly={readonly}
                    />
                }
            </div>
        );

    } else {

        return (
            <Layout
                attribute={attribute}
                hideLabel={hideLabel}
                direction={direction}
                fieldPath={fieldPath}
                gridMode={gridMode}
                beforeAttributeControl={beforeAttributeControl}
                isClearable={!gridMode && !readonly}
                labelVerticalAlign="top"
            >
                <div className="tw-flex tw-flex-row">
                    {!attribute.params?.hideAssetFolderBtn && <IconButton
                        className="input tw-px-3 tw-flex tw-items-center tw-bg-gray-100 tw-whitespace-nowrap tw--mt-px tw-mb-px"
                        onClick={openAssetFoldersModal}
                        icon={FolderOpenIcon}
                        type="button"
                        disabled={readonly}
                        size="md"
                    />}

                    {/* The actual data model of the field */}
                    <input
                        id={fieldPath}
                        className={`tw-hidden ${attribute.name}-upload-input`}
                        disabled={readonly}
                        {...register(fieldPath)}
                    />

                    {/* The "view" model of the field that changes the viewed Url on hover */}
                    <Input
                        id={`view-model-${fieldPath}`}
                        className={`${isDragActive ? 'tw-bg-gray-200' : ''} ${hasErrors ? 'error-input' : hasWarnings ? 'warning-input' : ''} tw-bg-white ${attribute.name}-upload-input`}
                        placeholder={placeholder}
                        disabled={readonly || attribute.params?.disableDragAndDrop}
                        value={currentValue}
                        onChange={onViewModelChange}
                        onMouseEnter={showFullUrl}
                        {...dropZoneProps}
                        ref={focusRef}
                    />

                    <input className="tw-hidden" {...dropZoneInputProps} />
                </div>

                {loadingPreview && (currentValue) && (
                    <div className="tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mt-3 tw-animate-pulse tw-rounded-md tw-bg-gray-100 tw-h-[400px] tw-w-[400px]">
                        <Loader />
                    </div>
                )}

                {!loadingPreview &&
                    (imageLoadError ? (
                        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-mx-auto tw-mt-3 tw-rounded-md tw-bg-gray-100 tw-h-[400px] tw-w-[400px]">
                            <Icon
                                icon={ExclamationTriangleIcon}
                                size="lg"
                                variant="destructive"
                                colorScheme="dark"
                            />
                            <span className="tw-text-error-dark">
                                {t('FILE_NOT_FOUND')}
                            </span>
                        </div>
                    ) : (
                        (currentValue) &&
                        (isPreviewUrlDrawable ? (
                            <div className="tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mt-3 tw-rounded-md tw-bg-gray-100 tw-h-[400px] tw-w-[400px] tw-p-6">
                                <Image
                                    className="tw-w-full tw-h-full tw-object-contain tw-cursor-pointer"
                                    alt={fieldPath}
                                    src={currentValue}
                                    onError={onLoadingImageError}
                                    onClick={() => setIsPreviewDialogOpen(true)}
                                />
                                {isPreviewDialogOpen && (
                                    <PreviewDialog
                                        url={currentValue}
                                        onClose={() =>
                                            setIsPreviewDialogOpen(false)
                                        }
                                    />
                                )}
                            </div>
                        ) : (
                            <a
                                href={currentValue}
                                target="_blank"
                                rel="noreferrer"
                                className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-mx-auto tw-mt-3 tw-rounded-md tw-bg-gray-100 tw-h-[400px] tw-w-[400px]"
                            >
                                <NonDrawableURLPreview mimeType={mimeType} className='tw-scale-[2]'/>
                                <span className="tw-mt-3 tw-w-full tw-text-center tw-truncate">
                                    {getFilenameFromUrl(currentValue)}
                                </span>
                            </a>
                        ))
                    ))}
            </Layout>
        );
    }

};

export { ACCEPTED_IMAGE_TYPES, ImageComponent };
